import { buttonClasses, cn, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { NEW_ACTIONS } from '../navData.js';
import { Flyout } from './Flyout.js';

/**
 * The bar's one primary action. It was a single `New job` button, which named the
 * most common act and hid the other three behind a list page — a person who wanted
 * to raise an invoice had to go to Invoices to find the button that does it.
 *
 * It opens on HOVER where the pointer can hover and on TAP where it cannot, and
 * below `sm` the panel is a sheet on the bottom edge rather than a 216px dropdown
 * pinned to a corner no thumb reaches. Both behaviours live in `Flyout`, so the
 * launcher and the account menu can have them without a second menu primitive.
 *
 * ⛔ Each item is gated by the guard on its OWN endpoint, read off the api worker —
 * see the rules and the reasoning in `NEW_ACTIONS`.
 */

// A menu row is LABEL-led — the same rule the account menu's rows follow, and the
// same 44px target every control in this chrome gets (A11Y-01).
const ITEM_CLASS =
  'flex h-11 w-full items-center gap-2.5 rounded-md px-2.5 text-sm font-medium text-foreground ' +
  'transition-colors duration-[var(--dur-quick)] ease-[var(--ease-out)] motion-reduce:transition-none ' +
  'hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 ' +
  'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-popover';

export function NewMenu() {
  const { t } = useTranslation();
  const canAccess = useAccess();
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const actions = NEW_ACTIONS.filter((action) => canAccess(action.access));

  /*
   * The letter beside each row is a promise, so it has to work. It is menu-scoped —
   * a bare letter, not a chord, which is what a bare letter means in a menu — and it
   * is armed only while the panel is open.
   *
   * The key CLICKS the row rather than navigating on its own. Navigating directly
   * left the panel standing open behind the new page: the panel's open state lives in
   * `Flyout`, and this component only learns of it. Clicking the row takes the path
   * every other dismissal takes, so the key and the mouse cannot drift apart.
   *
   * The editable-element guard is not theoretical: this flyout is NON-MODAL, so the
   * page behind it stays live and a caret can still be sitting in a field while the
   * menu is open. Without the guard, typing "job" into that field would navigate.
   */
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
      ) {
        return;
      }
      const row = listRef.current?.querySelector<HTMLAnchorElement>(
        `[data-new-key="${CSS.escape(event.key.toLowerCase())}"]`,
      );
      if (!row) return;
      event.preventDefault();
      row.click();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // A person who may create nothing gets no button, not a button over an empty panel.
  if (actions.length === 0) return null;

  return (
    <Flyout
      label={t('nav:new.label')}
      panelLabel={t('nav:new.openMenu')}
      onOpenChange={setOpen}
      trigger={
        <>
          <span>{t('nav:new.label')}</span>
          <Plus aria-hidden="true" />
        </>
      }
      /*
       * The kit's real primary-button class string, not a copy of it. The trigger
       * cannot BE a `<Button>` — `Flyout` renders the element and owns its ref, its
       * `aria-expanded` and its click — so it borrows the look from the one place
       * that defines it.
       */
      triggerBase={buttonClasses({ variant: 'primary' })}
      triggerClassName={cn(
        // The label never steps aside. On a 390px bar the search field is the only
        // thing allowed to shrink; this is the bar's primary action and hiding it
        // to save 34px would cost a phone user the thing they came to press.
        'shrink-0 px-3 sm:px-4',
        // The open state is the hover state, held. A custom trigger base owns this —
        // the chrome button's grey open tone would repaint the fill on open.
        'aria-expanded:bg-primary/90',
      )}
      panelClassName={cn(
        'right-0 origin-top-right w-56 max-w-[calc(100vw-1.5rem)]',
        // Below `sm` it leaves the bar: a sheet on the bottom edge, full width, square
        // at the bottom because there is no page below it to round away from. It sits
        // over the scrim `Flyout` draws for it.
        'max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:mt-0 max-sm:w-auto',
        'max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:p-2',
        'max-sm:pb-[calc(0.5rem+env(safe-area-inset-bottom))] max-sm:origin-bottom',
      )}
      openOnHover
      sheetScrim
    >
      <div ref={listRef} className="flex flex-col gap-0.5">
        {actions.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.to}
              to={action.to}
              data-new-key={action.key}
              /* The row is named by its LABEL alone. Without this the letter beside it
                 joins the accessible name and the row announces as "Job j". */
              aria-label={t(action.labelKey)}
              className={ITEM_CLASS}
            >
              <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span>{t(action.labelKey)}</span>
              <kbd className="ml-auto font-mono text-muted-foreground text-xs uppercase max-sm:hidden">
                {action.key}
              </kbd>
            </Link>
          );
        })}
      </div>
    </Flyout>
  );
}
